import { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Archive, Download, FileUp, FolderTree, Plus, RotateCcw, Search, Tags } from "lucide-react";

const EMPTY_INDUSTRY = { name: "", region: "" };
const EMPTY_TITLE = { name: "", industry_id: "", region: "" };

export default function RACatalog() {
  const { can } = useAuth();
  const canManage = can("ra:catalog_manage");
  const [summary, setSummary] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [titles, setTitles] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState("all");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("active");
  const [industryDialog, setIndustryDialog] = useState(false);
  const [titleDialog, setTitleDialog] = useState(false);
  const [importDialog, setImportDialog] = useState(false);
  const [preview, setPreview] = useState(null);
  const [industryForm, setIndustryForm] = useState(EMPTY_INDUSTRY);
  const [titleForm, setTitleForm] = useState(EMPTY_TITLE);
  const [editingIndustry, setEditingIndustry] = useState(null);
  const [editingTitle, setEditingTitle] = useState(null);
  const [upload, setUpload] = useState(null);
  const [importFormat, setImportFormat] = useState("auto");
  const [importMode, setImportMode] = useState("add_new");

  const load = useCallback(async () => {
    try {
      const [summaryRes, industriesRes, titlesRes] = await Promise.all([
        api.get("/ra-catalog/summary"),
        api.get("/ra-catalog/industries", { params: { status, q: query || undefined } }),
        api.get("/ra-catalog/titles", {
          params: {
            status,
            q: query || undefined,
            industry_id: selectedIndustry === "all" ? undefined : selectedIndustry,
          },
        }),
      ]);
      setSummary(summaryRes.data);
      setIndustries(industriesRes.data.items);
      setTitles(titlesRes.data.items);
    } catch (error) {
      toast.error(apiErr(error));
    }
  }, [query, selectedIndustry, status]);

  useEffect(() => {
    load();
  }, [load]);

  const openIndustry = (industry = null) => {
    setEditingIndustry(industry);
    setIndustryForm(industry ? { name: industry.name, region: industry.region || "" } : EMPTY_INDUSTRY);
    setIndustryDialog(true);
  };

  const openTitle = (title = null) => {
    setEditingTitle(title);
    setTitleForm(title ? {
      name: title.name,
      industry_id: title.industry_id,
      region: title.region || "",
    } : { ...EMPTY_TITLE, industry_id: selectedIndustry === "all" ? "" : selectedIndustry });
    setTitleDialog(true);
  };

  const saveIndustry = async () => {
    if (!industryForm.name.trim()) {
      toast.error("An industry name is required");
      return;
    }
    try {
      if (editingIndustry) {
        await api.put(`/ra-catalog/industries/${editingIndustry.id}`, industryForm);
      } else {
        await api.post("/ra-catalog/industries", industryForm);
      }
      setIndustryDialog(false);
      toast.success(editingIndustry ? "Industry updated" : "Industry added");
      load();
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  const saveTitle = async () => {
    if (!titleForm.name.trim() || !titleForm.industry_id) {
      toast.error("A title and industry are required");
      return;
    }
    try {
      if (editingTitle) {
        await api.put(`/ra-catalog/titles/${editingTitle.id}`, titleForm);
      } else {
        await api.post("/ra-catalog/titles", titleForm);
      }
      setTitleDialog(false);
      toast.success(editingTitle ? "Title updated" : "Title added");
      load();
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  const changeStatus = async (kind, item) => {
    const action = item.status === "active" ? "archive" : "restore";
    try {
      await api.post(`/ra-catalog/${kind}/${item.id}/${action}`);
      toast.success(action === "archive" ? "Archived" : "Restored");
      load();
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  const createPreview = async () => {
    if (!upload) {
      toast.error("Choose a CSV or XLSX catalog file first");
      return;
    }
    const data = new FormData();
    data.append("file", upload);
    data.append("import_format", importFormat);
    try {
      const result = await api.post("/ra-catalog/imports/preview", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(result.data);
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  const commitImport = async () => {
    try {
      const { data } = await api.post(`/ra-catalog/imports/${preview.id}/commit`, {
        mode: importMode,
      });
      toast.success(`${data.created_titles} titles imported; ${data.skipped} skipped`);
      setPreview(null);
      setUpload(null);
      setImportDialog(false);
      load();
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  const download = async (path, filename) => {
    try {
      const response = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="R.A. Catalog Desk"
        subtitle="One shared industry and title catalog for authorized R.A. teams"
        actions={canManage && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => download("/ra-catalog/template", "ra_catalog_template.csv")} data-testid="catalog-template-download">
              <Download className="mr-1.5 h-4 w-4" /> Template
            </Button>
            <Button variant="outline" onClick={() => download("/ra-catalog/export", "nexreq_ra_catalog.csv")} data-testid="catalog-export-button">
              <Download className="mr-1.5 h-4 w-4" /> Export
            </Button>
            <Button variant="outline" onClick={() => setImportDialog(true)} data-testid="catalog-import-button">
              <FileUp className="mr-1.5 h-4 w-4" /> Import Catalog
            </Button>
            <Button onClick={() => openTitle()} data-testid="catalog-add-title-button" className="bg-[#255058] text-white hover:bg-[#1c3d43]">
              <Plus className="mr-1.5 h-4 w-4" /> Add Title
            </Button>
          </div>
        )}
      />

      <div className="grid gap-3 sm:grid-cols-3" data-testid="catalog-summary">
        <Metric icon={FolderTree} label="Active industries" value={summary?.industries ?? "—"} />
        <Metric icon={Tags} label="Active titles" value={summary?.titles ?? "—"} />
        <Metric icon={FileUp} label="Catalog versions" value={summary?.version ?? "—"} />
      </div>

      <Card className="p-3" data-testid="catalog-filters">
        <div className="grid gap-2 md:grid-cols-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9" placeholder="Search catalog" data-testid="catalog-search" />
          </div>
          <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
            <SelectTrigger data-testid="catalog-industry-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All industries</SelectItem>
              {industries.map((industry) => <SelectItem key={industry.id} value={industry.id}>{industry.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="catalog-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active records</SelectItem>
              <SelectItem value="archived">Archived records</SelectItem>
              <SelectItem value="all">All records</SelectItem>
            </SelectContent>
          </Select>
          {canManage && <Button variant="outline" onClick={() => openIndustry()} data-testid="catalog-add-industry-button"><Plus className="mr-1.5 h-4 w-4" /> Add Industry</Button>}
        </div>
      </Card>

      <div className="grid gap-5 xl:grid-cols-[320px_minmax(0,1fr)]">
        <Card className="overflow-hidden" data-testid="catalog-industries-panel">
          <div className="border-b border-border px-4 py-3 font-display font-semibold">Industries</div>
          <div className="max-h-[58vh] divide-y divide-border overflow-y-auto">
            {industries.map((industry) => (
              <button key={industry.id} onClick={() => setSelectedIndustry(industry.id)} data-testid={`catalog-industry-${industry.id}`} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-accent/50">
                <div className="min-w-0 flex-1"><div className="truncate font-medium">{industry.name}</div><div className="text-xs text-muted-foreground">{industry.title_count} active titles · {industry.region || "Global"}</div></div>
                <StatusBadge value={industry.status} />
              </button>
            ))}
            {!industries.length && <EmptyState icon={FolderTree} title="No matching industries" />}
          </div>
        </Card>

        <Card className="overflow-hidden" data-testid="catalog-titles-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
            <div><div className="font-display font-semibold">Job Titles</div><div className="text-xs text-muted-foreground">{titles.length} visible titles</div></div>
            {canManage && <Button size="sm" onClick={() => openTitle()} data-testid="catalog-table-add-title"><Plus className="mr-1.5 h-4 w-4" /> Add Title</Button>}
          </div>
          {!titles.length ? <EmptyState icon={Tags} title="No titles match these filters" /> : <Table>
            <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Industry</TableHead><TableHead className="hidden lg:table-cell">Region</TableHead><TableHead>Status</TableHead>{canManage && <TableHead>Actions</TableHead>}</TableRow></TableHeader>
            <TableBody>{titles.map((title) => <TableRow key={title.id} data-testid={`catalog-title-${title.id}`}><TableCell className="font-medium">{title.name}</TableCell><TableCell>{title.industry_name}</TableCell><TableCell className="hidden lg:table-cell">{title.region || "Global"}</TableCell><TableCell><StatusBadge value={title.status} /></TableCell>{canManage && <TableCell><div className="flex gap-1"><Button size="sm" variant="ghost" onClick={() => openTitle(title)} data-testid={`catalog-edit-title-${title.id}`}>Edit</Button><Button size="sm" variant="ghost" onClick={() => changeStatus("titles", title)} data-testid={`catalog-toggle-title-${title.id}`}>{title.status === "active" ? <Archive className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}</Button></div></TableCell>}</TableRow>)}</TableBody>
          </Table>}
        </Card>
      </div>

      <CatalogDialog open={industryDialog} setOpen={setIndustryDialog} title={editingIndustry ? "Edit Industry" : "Add Industry"} onSave={saveIndustry} saveId="catalog-industry-save">
        <Label htmlFor="catalog-industry-name">Industry name</Label><Input id="catalog-industry-name" value={industryForm.name} onChange={(e) => setIndustryForm({ ...industryForm, name: e.target.value })} data-testid="catalog-industry-name" />
        <Label htmlFor="catalog-industry-region">Region</Label><Input id="catalog-industry-region" value={industryForm.region} onChange={(e) => setIndustryForm({ ...industryForm, region: e.target.value })} data-testid="catalog-industry-region" placeholder="Global by default" />
      </CatalogDialog>

      <CatalogDialog open={titleDialog} setOpen={setTitleDialog} title={editingTitle ? "Edit Job Title" : "Add Job Title"} onSave={saveTitle} saveId="catalog-title-save">
        <Label htmlFor="catalog-title-name">Job title</Label><Input id="catalog-title-name" value={titleForm.name} onChange={(e) => setTitleForm({ ...titleForm, name: e.target.value })} data-testid="catalog-title-name" />
        <Label>Industry</Label><Select value={titleForm.industry_id} onValueChange={(industry_id) => setTitleForm({ ...titleForm, industry_id })}><SelectTrigger data-testid="catalog-title-industry"><SelectValue placeholder="Select industry" /></SelectTrigger><SelectContent>{industries.filter((industry) => industry.status === "active").map((industry) => <SelectItem key={industry.id} value={industry.id}>{industry.name}</SelectItem>)}</SelectContent></Select>
        <Label htmlFor="catalog-title-region">Region</Label><Input id="catalog-title-region" value={titleForm.region} onChange={(e) => setTitleForm({ ...titleForm, region: e.target.value })} data-testid="catalog-title-region" placeholder="Global by default" />
      </CatalogDialog>

      <Dialog open={importDialog} onOpenChange={setImportDialog}>
        <DialogContent className="max-w-3xl"><DialogHeader><DialogTitle>Import R.A. Catalog</DialogTitle><DialogDescription>Preview every CSV/XLSX file before the global catalog changes. Google Sheet import activates after secure OAuth configuration.</DialogDescription></DialogHeader>
          {!preview ? <div className="space-y-4"><Input type="file" accept=".csv,.xlsx" onChange={(e) => setUpload(e.target.files?.[0] || null)} data-testid="catalog-import-file" /><Select value={importFormat} onValueChange={setImportFormat}><SelectTrigger data-testid="catalog-import-format"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="auto">Auto-detect format</SelectItem><SelectItem value="column">Industry-by-Column Catalog Import</SelectItem><SelectItem value="row">Row-based import</SelectItem></SelectContent></Select></div> : <ImportPreview preview={preview} mode={importMode} setMode={setImportMode} />}
          <DialogFooter><Button variant="outline" onClick={() => { setPreview(null); setImportDialog(false); }} data-testid="catalog-import-cancel">Cancel</Button>{preview ? <Button onClick={commitImport} data-testid="catalog-import-commit" className="bg-[#255058] text-white hover:bg-[#1c3d43]">Commit Import</Button> : <Button onClick={createPreview} data-testid="catalog-import-preview" className="bg-[#255058] text-white hover:bg-[#1c3d43]">Preview Import</Button>}</DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return <Card className="p-4"><div className="flex items-center gap-3"><Icon className="h-5 w-5 text-[#255058]" /><div><div className="font-display text-2xl font-bold">{value}</div><div className="text-xs text-muted-foreground">{label}</div></div></div></Card>;
}

function CatalogDialog({ children, onSave, open, saveId, setOpen, title }) {
  return <Dialog open={open} onOpenChange={setOpen}><DialogContent><DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader><div className="space-y-3">{children}</div><DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={onSave} data-testid={saveId} className="bg-[#255058] text-white hover:bg-[#1c3d43]">Save</Button></DialogFooter></DialogContent></Dialog>;
}

function ImportPreview({ mode, preview, setMode }) {
  const c = preview.counts;
  return <div className="space-y-4"><div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4"><PreviewStat label="Worksheet" value={preview.worksheet} /><PreviewStat label="Format" value={preview.format === "column" ? "Industry-by-Column" : "Row-based"} /><PreviewStat label="Industries found" value={new Set(preview.records.map((record) => record.industry)).size} /><PreviewStat label="Titles found" value={preview.records.length} /><PreviewStat label="New industries" value={c.new_industries} /><PreviewStat label="New titles" value={c.new_titles} /><PreviewStat label="Exact duplicates" value={c.exact_duplicates} /><PreviewStat label="Blank cells ignored" value={preview.blank_cells_ignored} /></div><div className="text-sm text-muted-foreground">{preview.invalid.length} invalid records · {c.possible_duplicates.length} possible duplicates requiring review</div><Select value={mode} onValueChange={setMode}><SelectTrigger data-testid="catalog-import-mode"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="add_new">Add New Records Only</SelectItem><SelectItem value="add_update">Add New and Update Existing</SelectItem><SelectItem value="selected">Import Selected Records</SelectItem></SelectContent></Select></div>;
}

function PreviewStat({ label, value }) {
  return <div className="border-l-2 border-[#255058]/25 pl-2"><div className="font-display font-semibold">{value}</div><div className="text-xs text-muted-foreground">{label}</div></div>;
}